import React from "react";
import { CHART, diamondPoints, trianglePoints } from "./config";
import { fitText, textWidth } from "./measureText";

function chartHistoricity(item) {
  const label = item.historicityLabel?.toLowerCase() || "";
  if (label.startsWith("mythological")) return "mythological";
  if (label.startsWith("legendary")) return "legendary";
  if (label.startsWith("literary") && label.includes("legendary")) return "literary / legendary";
  return null;
}

function RowFrame({ children, item, periodColor, showPeriod, testId, y }) {
  // Alternate rows are tinted a shade off the ground. The chart is 1290 units
  // wide and a mark can sit at the far right of it, so the eye needs something
  // to carry a name across to its bar; the row rules alone were not enough.
  // Slight on purpose — anything stronger reads as a selection.
  const banded = Math.round((y - CHART.top) / CHART.rowHeight) % 2 === 1;

  return (
    <g
      data-testid={testId}
      data-start-year={item.startYear}
      data-entry-type={item.entryType || item.kind}
      data-row-kind={item.kind === "esoteric" ? item.tradition : item.kind}
      data-banded={banded ? "true" : "false"}
    >
      {banded && (
        <rect
          className="history-chart__row-band"
          x="0"
          // Tiled exactly between the row rules: the rule at y + 14 is this
          // band's bottom edge and the next row's top.
          y={y + 14 - CHART.rowHeight}
          width={CHART.width - CHART.rightPadding}
          height={CHART.rowHeight}
        />
      )}
      {showPeriod && (
        <line
          className="history-chart__period-band"
          x1="0"
          y1={y}
          x2={CHART.width - CHART.rightPadding}
          y2={y}
          stroke={periodColor}
        />
      )}
      <line
        className="history-chart__row-rule"
        x1="0"
        y1={y + 14}
        x2={CHART.width - CHART.rightPadding}
        y2={y + 14}
      />
      {children}
    </g>
  );
}

// The names, the dates and the period dot, held against the left edge while
// the chart scrolls under them.
//
// SVG children cannot be `position: sticky`, so the shift is applied as a CSS
// custom property set on the <svg> by TimelineSvg — a property, not a React
// prop, so a scroll does not re-render eighty rows to move one column.
//
// It renders LAST inside each row's anchor so it paints over that row's own
// bar. Rows never overlap vertically, so a per-row order is enough; the
// backdrop then only has to hide the bar sliding beneath it.
function PinnedLabels({ children, dates, name, nameClass, showPeriod, y }) {
  // The dot sits where the name would otherwise start, so the name steps aside
  // for it and reclaims the room when the period layer is off.
  const nameX = showPeriod ? CHART.nameX : 0;
  // What the dates leave. A long "mythological · 3000-2500 BCE" takes the room
  // out of the name rather than running into it, which is the collision the
  // browser suite watches for.
  const room = CHART.dateX - nameX
    - textWidth(dates, { size: CHART.dateSize })
    - CHART.nameGap;

  return (
    <g className="history-chart__labels">
      <rect
        className="history-chart__label-backdrop"
        x={-2}
        y={y - 16}
        width={CHART.labelGutter - 6}
        height="30"
      />
      {/* Reads as the edge of a held column rather than a gap in the bar. */}
      <line
        className="history-chart__label-edge"
        x1={CHART.labelGutter - 8}
        y1={y - 16}
        x2={CHART.labelGutter - 8}
        y2={y + 14}
      />
      {children}
      <text className={nameClass} x={nameX} y={y + 5}>
        {fitText(name, room, { size: CHART.nameSize, weight: 600 })}
      </text>
      <text className="history-chart__dates" x={CHART.dateX} y={y + 5} textAnchor="end">
        {dates}
      </text>
    </g>
  );
}

function PeriodDot({ color, show, x = 6, y }) {
  if (!show) return null;
  return <circle className="history-chart__period-dot" cx={x} cy={y} r="4" fill={color} />;
}

function CulturalMark({ item, x, y }) {
  const pointClass = `history-chart__cultural-point history-chart__${item.tradition}-point`;
  if (item.point && item.entryType === "figure") {
    return (
      <polygon
        className={`${pointClass} history-chart__cultural-point--${item.datingBasis}`}
        points={trianglePoints(x(item.startYear), y)}
      />
    );
  }
  if (item.point && item.entryType === "event") {
    return <polygon className={pointClass} points={diamondPoints(x(item.startYear), y)} />;
  }
  if (item.point) {
    return (
      <rect className={pointClass} x={x(item.startYear) - 4} y={y - 4} width="8" height="8" />
    );
  }

  return (
    <>
      <line
        className={`history-chart__cultural-span history-chart__${item.tradition}-span history-chart__cultural-span--${item.entryType}`}
        x1={x(item.startYear)}
        y1={y}
        x2={x(item.endYear)}
        y2={y}
      />
      {[item.startYear, item.endYear].map((year, index) => (
        <rect
          key={`${year}-${index}`}
          className={`history-chart__cultural-endpoint history-chart__${item.tradition}-point`}
          x={x(year) - 3}
          y={y - 3}
          width="6"
          height="6"
        />
      ))}
    </>
  );
}

function CulturalRow({ handlersFor, item, periodColor, showPeriod, x, y }) {
  const destination = item.informationUrl || item.profileHref || item.sourceUrl;
  const external = destination.startsWith("https://");
  const historicity = chartHistoricity(item);

  return (
    <RowFrame
      item={item}
      periodColor={periodColor}
      showPeriod={showPeriod}
      testId={`history-${item.tradition}-${item.slug}`}
      y={y}
    >
      <a
        className="history-chart__item-link"
        href={destination}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
        aria-label={`${item.name}, ${item.dates}. ${item.historicityLabel ? `${item.historicityLabel}. ` : ""}${item.traditionLabel}; ${item.typeLabel}. More information. Research source: ${item.sourceLabel}`}
        {...handlersFor(item)}
      >
        <CulturalMark item={item} x={x} y={y} />
        <PinnedLabels
          dates={`${historicity ? `${historicity} · ` : ""}${item.chartDates}`}
          name={item.name}
          nameClass={`history-chart__name history-chart__cultural-name history-chart__${item.tradition}-name`}
          showPeriod={showPeriod}
          y={y}
        >
          <PeriodDot color={periodColor} show={showPeriod} y={y} />
        </PinnedLabels>
      </a>
    </RowFrame>
  );
}

function EventMark({ item, x, y }) {
  if (item.point) {
    return <polygon className="history-chart__event-point" points={diamondPoints(x(item.startYear), y)} />;
  }

  return (
    <>
      <line
        className="history-chart__event-span"
        x1={x(item.startYear)}
        y1={y}
        x2={x(item.endYear)}
        y2={y}
      />
      <polygon className="history-chart__event-point" points={diamondPoints(x(item.startYear), y, 4)} />
      <polygon className="history-chart__event-point" points={diamondPoints(x(item.endYear), y, 4)} />
    </>
  );
}

function EventRow({ handlersFor, item, periodColor, showPeriod, x, y }) {
  return (
    <RowFrame
      item={item}
      periodColor={periodColor}
      showPeriod={showPeriod}
      testId={`history-event-${item.slug}`}
      y={y}
    >
      <a
        className="history-chart__item-link"
        href={item.sourceUrl}
        target="_blank"
        rel="noreferrer"
        aria-label={`${item.name}, ${item.dates}. Source: ${item.sourceLabel}`}
        {...handlersFor(item)}
      >
        <EventMark item={item} x={x} y={y} />
        <PinnedLabels
          dates={item.dates}
          name={item.name}
          nameClass="history-chart__name history-chart__event-name"
          showPeriod={showPeriod}
          y={y}
        >
          <PeriodDot color={periodColor} show={showPeriod} y={y} />
        </PinnedLabels>
      </a>
    </RowFrame>
  );
}

function FigureMark({ item, periodColor, x, y }) {
  const point = item.dating === "floruit" || item.startYear === item.endYear;
  const colorStyle = periodColor ? { stroke: periodColor } : undefined;
  if (point) {
    return (
      <circle
        className={`history-chart__point history-chart__point--${item.dating}`}
        cx={x(item.startYear)}
        cy={y}
        r="4"
        style={periodColor ? {
          stroke: periodColor,
          fill: item.dating === "floruit" ? periodColor : undefined
        } : undefined}
      />
    );
  }

  return (
    <>
      <line
        className={`history-chart__life history-chart__life--${item.dating}`}
        x1={x(item.startYear)}
        y1={y}
        x2={x(item.endYear)}
        y2={y}
        style={colorStyle}
      />
      <circle className="history-chart__endpoint" cx={x(item.startYear)} cy={y} r="2.2" style={colorStyle} />
      <circle className="history-chart__endpoint" cx={x(item.endYear)} cy={y} r="2.2" style={colorStyle} />
    </>
  );
}

function FigureRow({ handlersFor, item, periodColor, showAdditional, showPeriod, x, y }) {
  const supporting = showAdditional && !item.spine ? " history-chart__name--supporting" : "";

  return (
    <RowFrame
      item={item}
      periodColor={periodColor}
      showPeriod={showPeriod}
      testId={`history-figure-${item.slug}`}
      y={y}
    >
      <a
        className="history-chart__item-link"
        href={item.href}
        aria-label={`${item.name}, ${item.dates}; ${item.eraLabel}; ${item.spine ? "central figure" : "additional figure"}`}
        {...handlersFor(item)}
      >
        <FigureMark item={item} periodColor={periodColor} x={x} y={y} />
        <PinnedLabels
          dates={item.dates}
          name={item.name}
          nameClass={`history-chart__name${supporting}`}
          showPeriod={showPeriod}
          y={y}
        >
          <PeriodDot color={periodColor} show={showPeriod} x={5} y={y} />
        </PinnedLabels>
      </a>
    </RowFrame>
  );
}

export default function TimelineRow({ handlersFor, item, periodMap, state, x, y }) {
  const periodColor = state.periods
    ? periodMap.get(item.periodSlug || item.era)?.color
    : undefined;
  const shared = { handlersFor, item, periodColor, showPeriod: state.periods, x, y };

  if (item.kind === "esoteric") return <CulturalRow {...shared} />;
  if (item.kind === "event") return <EventRow {...shared} />;
  return <FigureRow {...shared} showAdditional={state.allFigures} />;
}
